import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Pencil } from "lucide-react";
import { ImportButton } from "@/components/ImportDialog";
import { toast } from "sonner";

const ESTADO_ID = { confirmado: "bg-emerald-100 text-emerald-700", provisorio: "bg-amber-100 text-amber-700", em_verificacao: "bg-violet-100 text-violet-700" };

export default function Clientes() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ nome: "", tipo: "oficina", nif: "", telefone: "", email: "", localidade: "", desconto_habitual: 0, estado_identidade: "provisorio" });

  const load = () => api.get("/clientes", { params: { q } }).then((r) => setList(r.data));
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const emptyForm = { nome: "", tipo: "oficina", nif: "", telefone: "", email: "", localidade: "", desconto_habitual: 0, estado_identidade: "provisorio" };
  const openCreate = () => { setEditId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({ nome: c.nome || "", tipo: c.tipo || "oficina", nif: c.nif || "", telefone: c.telefone || "", email: c.email || "", localidade: c.localidade || "", desconto_habitual: c.desconto_habitual || 0, estado_identidade: c.estado_identidade || "provisorio" });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...form, desconto_habitual: parseFloat(form.desconto_habitual) || 0 };
      if (editId) { await api.put(`/clientes/${editId}`, payload); toast.success("Cliente atualizado."); }
      else { await api.post("/clientes", payload); toast.success("Cliente criado."); }
      setOpen(false);
      setForm(emptyForm);
      setEditId(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-900">Clientes</h1>
        <div className="flex gap-2">
          <ImportButton entity="clientes" label="clientes" onDone={load} />
          <Button className="bg-sky-600 hover:bg-sky-700 gap-1.5" data-testid="btn-novo-cliente" onClick={openCreate}><Plus size={16} /> Novo cliente</Button>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editId ? "Editar cliente" : "Novo cliente"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs">Nome</Label><Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="cliente-nome" /></div>
              <div><Label className="text-xs">Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="cliente-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="oficina">Oficina</SelectItem><SelectItem value="particular">Particular</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Estado identidade</Label>
                <Select value={form.estado_identidade} onValueChange={(v) => setForm({ ...form, estado_identidade: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="confirmado">Confirmado</SelectItem><SelectItem value="provisorio">Provisório</SelectItem><SelectItem value="em_verificacao">Em verificação</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">NIF</Label><Input value={form.nif} onChange={(e) => setForm({ ...form, nif: e.target.value })} /></div>
              <div><Label className="text-xs">Telefone</Label><Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} /></div>
              <div><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label className="text-xs">Desconto habitual %</Label><Input type="number" value={form.desconto_habitual} onChange={(e) => setForm({ ...form, desconto_habitual: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button className="bg-sky-600 hover:bg-sky-700" onClick={save} data-testid="btn-guardar-cliente">{editId ? "Atualizar" : "Guardar"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mb-3 max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar nome, NIF, telefone…" className="pl-9" data-testid="clientes-search" />
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
            <tr><th className="text-left px-4 py-2 font-semibold">Nome</th><th className="text-left px-4 py-2 font-semibold">Tipo</th><th className="text-left px-4 py-2 font-semibold">NIF</th><th className="text-left px-4 py-2 font-semibold">Telefone</th><th className="text-right px-4 py-2 font-semibold">Desc.</th><th className="text-left px-4 py-2 font-semibold">Identidade</th><th className="text-right px-4 py-2 font-semibold">Ações</th></tr>
          </thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`cliente-row-${c.id}`}>
                <td className="px-4 py-2 font-medium text-slate-800">{c.nome}</td>
                <td className="px-4 py-2 capitalize text-slate-600">{c.tipo}</td>
                <td className="px-4 py-2 font-mono text-slate-600">{c.nif || "—"}</td>
                <td className="px-4 py-2 text-slate-600">{c.telefone || "—"}</td>
                <td className="px-4 py-2 text-right">{c.desconto_habitual ? `${c.desconto_habitual}%` : "—"}</td>
                <td className="px-4 py-2"><span className={`text-[10px] px-2 py-0.5 rounded-full ${ESTADO_ID[c.estado_identidade]}`}>{c.estado_identidade}</span></td>
                <td className="px-4 py-2 text-right"><Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => openEdit(c)} data-testid={`cliente-edit-${c.id}`}><Pencil size={12} /> Editar</Button></td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={7} className="text-center text-slate-400 py-8">Sem clientes.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
